/**
 * @module config/tier
 * @description Which demo tier this build is, and what each tier switches on.
 *
 * Tiers COMPOSE upward: Enhanced = every Standard feature + its own. That is
 * expressed by spreading STANDARD_FEATURES into ENHANCED_FEATURES, so a
 * Standard feature can never be missing from Enhanced by accident.
 * Immersive is a different experience (Send Love Live) that reuses the same
 * data and tokens; its flags select that shell.
 *
 * Every flag maps to a Q1314 line item, so the demo visibly shows what each
 * tier's quote promises.
 */

/** The three tiers, in price order. Mirrored in vite.config.ts (TIERS). */
export const TIERS = ["standard", "enhanced", "immersive"] as const;
export type Tier = (typeof TIERS)[number];

/** Feature switches. Each comment names the Q1314 line it demonstrates. */
export interface TierFeatures {
    /** Shell: "browse" (Standard/Enhanced page) or "stages" (Send Love Live). */
    shell: "browse" | "stages";
    /** Q1314 T2: one-question-per-screen checkout with progress. Off = single-page checkout (T1). */
    guidedCheckout: boolean;
    /** Q1314 T2: page transitions, skeletons, staggered entrance. Off = brief §4.9 hover/zoom only. */
    motionSystem: boolean;
    /** Q1314 T2: "Match me to a cause" guide. */
    matchMe: boolean;
    /** Q1314 T2: saved causes persist across visits. Off = saved for this visit only (T1 brief §4.4). */
    savedCausesPersist: boolean;
}

/** Tier 1: exactly AFJ's brief. */
const STANDARD_FEATURES: TierFeatures = {
    shell: "browse",
    guidedCheckout: false,
    motionSystem: false,
    matchMe: false,
    savedCausesPersist: false,
};

/** Tier 2: Standard + guided checkout and premium UX. */
const ENHANCED_FEATURES: TierFeatures = {
    ...STANDARD_FEATURES,
    guidedCheckout: true,
    motionSystem: true,
    matchMe: true,
    savedCausesPersist: true,
};

/** Tier 3: Send Love Live; inherits Enhanced's motion and persistence. */
const IMMERSIVE_FEATURES: TierFeatures = {
    ...ENHANCED_FEATURES,
    shell: "stages",
};

/** Lookup table, typed so a new tier without features fails to compile. */
export const TIER_FEATURES: Readonly<Record<Tier, TierFeatures>> = {
    standard: STANDARD_FEATURES,
    enhanced: ENHANCED_FEATURES,
    immersive: IMMERSIVE_FEATURES,
};

/** Display names, exactly as settled in Session 1C (do not reword). */
export const TIER_NAMES: Readonly<Record<Tier, string>> = {
    standard: "Standard: Brief-Matched Giving Page",
    enhanced: "Enhanced: Guided Checkout & Premium UX",
    immersive: "Immersive: Live, Gamified Giving Platform",
};

/**
 * Narrow an env string to a Tier.
 * @throws Error on an unknown value, so a mistyped build never ships the wrong tier.
 */
export function parseTier(value: string | undefined): Tier {
    const candidate: string = value ?? "standard";
    if ((TIERS as readonly string[]).includes(candidate)) {
        return candidate as Tier;
    }
    throw new Error(`[config/tier] Unknown VITE_AFJ_TIER "${candidate}". Expected one of: ${TIERS.join(", ")}.`);
}

/** The tier of THIS build (set by the package.json build/dev scripts). */
export const CURRENT_TIER: Tier = parseTier(import.meta.env.VITE_AFJ_TIER);

/** Feature flags of THIS build. */
export const FEATURES: TierFeatures = TIER_FEATURES[CURRENT_TIER];
